import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { ChannelApiProvider, useChannelApi } from '../Services/ChannelApiProvider';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { channelTypes } from '../constants/channelTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function ChannelsHub({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getChannels, deleteChannel } = useChannelApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [channels, setChannels] = useState([]);
  const [totalChannels, setTotalChannels] = useState(0);
  const [alertShown, setAlertShown] = useState(false);

  const tableTitle = 'Channels';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false },
    { name: 'Name', selector: (row) => row.name, sortable: false },
    { name: 'Channel', selector: (row) => channelTypes[row.channel_type], sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at), sortable: false },
  ];

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/channels/${row.id}`, { state: { organizationId } });
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteChannel(selectedId); // await deleteDocument
        await fetchChannels(storedSkip(), storedLimit());
        showAlert('success', 'Item was successfully deleted!'); // for now we will show success message
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchChannels = async (skip, limit) => {
    setLoading(true);
    try {
      const response = await getChannels(skip, limit);
      setChannels(response.items);
      setTotalChannels(response.totalCount);
      setError(null);
    } catch (err) {
      console.error('Error fetching channels:', err);
      setError('Failed to load channels');
      setChannels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels(storedSkip(), storedLimit());
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={channels}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalChannels}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchChannels}
                getRowUrl={(row) => `/organizations/${organizationId}/channels/${row.id}`}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/channels/new-channel`)}
                >
                  Create New Channel
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChannelsHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ChannelApiProvider>
        <ChannelsHub canEdit={canEdit} />
      </ChannelApiProvider>
    </ConfirmationProvider>
  );
}

export default ChannelsHubWrapper;
